import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BackdropCustom } from "../../../components/backdrop/Backdrop";
import { PageCard } from "../../../components/pageCard/PageCard";
import { CustomMenuItem } from "../../../components/table/CustomMenuItem/CustomMenuItem";
import { CustomNoContentTableBody } from "../../../components/table/CustomNoContentTableBody/CustomNoContentTableBody";
import { CustomTableCell } from "../../../components/table/CustomTableCell/CustomTableCell";
import CustomTablePagination from "../../../components/table/CustomTablePagination/CustomTablePagination";
import CustomTableSortLabel from "../../../components/table/CustomTableSortLabel/CustomTableSortLabel";
import { FilterButtonGroup } from "../../../components/table/FilterButtonGroup/FilterButtonGroup";
import { TitleContainer } from "../../../components/titleContainer/TitleContainer";
import { useToastr } from "../../../components/toast/Toast";
import { useAuth } from "../../../hooks/auth";
import api from "../../../services/api";

interface IExhibitionJudgesProps {
  exhibitionId: string;
  finished: boolean;
}

interface IJudge {
  id: string;
  name: string;
}

export const ExhibitionJudges: React.FC<IExhibitionJudgesProps> = ({
  exhibitionId,
  finished,
}) => {
  const toastr = useToastr();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [judges, setJudges] = useState<IJudge[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderField, setOrderField] = useState("name");
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(false);

  const loadJudges = useCallback(
    async (
      pPage: number,
      cPage: number,
      oField: string,
      oDirection: "asc" | "desc",
    ) => {
      if (!exhibitionId) return;
      setLoading(true);
      await api
        .get(
          `judgment-judges/exhibition/${exhibitionId}/judges?perPage=${pPage}&currentPage=${cPage + 1}&orderDirection=${oDirection.toUpperCase()}`,
        )
        .then(res => {
          setJudges(res.data.data || []);
          setTotalRows(res.data.totalRows || 0);
        })
        .catch(error => {
          if (error?.response?.status === 401) {
            signOut();
            navigate("/");
          }
          toastr.error(
            error?.response?.data?.message ||
              "Erro ao carregar juízes da exposição",
          );
        })
        .finally(() => setLoading(false));
    },
    [exhibitionId, signOut, navigate, toastr],
  );

  const handlePagination = async (
    pPage: number,
    cPage: number,
    oField: string,
    oDirection: "asc" | "desc",
  ) => {
    await loadJudges(pPage, cPage, oField, oDirection);
  };

  useEffect(() => {
    loadJudges(perPage, currentPage, orderField, orderDirection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadJudges]);

  const handleToAddJudgeRelation = () => {
    navigate(`/exhibition/${exhibitionId}/judgment-judges`);
  };

  const handleToEditJudgeRelation = (judgeId: string) => {
    navigate(`/exhibition/${exhibitionId}/judgment-judges?userId=${judgeId}`);
  };



  return (
    <>
      <TitleContainer>
        <h1>Juízes</h1>
      </TitleContainer>

      <PageCard>
        <FilterButtonGroup>
          <div></div>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Adicionar Juiz" placement="top">
              <IconButton
                onClick={handleToAddJudgeRelation}
                size="small"
                color="primary"
                disabled={finished}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </FilterButtonGroup>
        <TableContainer sx={{ minHeight: "250px" }}>
          <Table sx={{ padding: "16px" }}>
            <TableHead>
              <TableRow
                sx={{
                  "&:last-child td, &:last-child th": {
                    borderBottom: 1,
                    borderColor: "#E5E5E5",
                  },
                }}
              >
                <TableCell size="small" sx={{ width: "30px" }}>
                  Ações
                </TableCell>
                <CustomTableSortLabel
                  perPage={perPage}
                  currentPage={currentPage}
                  orderField={orderField}
                  field="name"
                  label="Nome do Juiz"
                  handleRequest={handlePagination}
                  orderDirection={orderDirection}
                  setField={setOrderField}
                  setDirection={setOrderDirection}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {judges.length > 0 &&
                judges.map(judge => (
                  <TableRow
                    key={judge.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <CustomMenuItem
                      id={judge.id}
                      handleToEdit={finished ? undefined : handleToEditJudgeRelation}
                      finished={finished}
                      width={20}
                    />
                    <CustomTableCell size="small" width={800}>
                      {judge.name}
                    </CustomTableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          {judges.length === 0 && (
            <CustomNoContentTableBody description="Nenhum juiz vinculado nesta exposição" />
          )}
        </TableContainer>

        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={handlePagination}
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
